/**
 * @format
 *
 */
import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { Store } from "@ngrx/store";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { combineLatest, Observable, Subscription } from "rxjs/index";
import { delay, filter, first, map } from "rxjs/operators";
import { BsModalConfirmComponent, createConfirmModalOptions } from "../../core/modal/modal-confirm.component";
import { Person } from "../../core/model/central/person.model";
import { DestinatorFilter } from "../message-filter/destinator-filter.model";
import { MessageFilterActions } from "../message-filter/state/message-filter.actions";
import { MessageFilterSelectors } from "../message-filter/state/message-filter.selectors";
import { PatientsActions } from "./state/patients.actions";
import { Destinator } from "./destinator-type";

interface ContentType {
   contentType: string;
   group: string;
   type: string;
   name: string;
   application: string;
   img: string;
   isSubscribed: boolean;
   destinatorFilterIds?: Array<number>;
}

export const availableContentTypes: Array<ContentType> = [
   {
      contentType: "relation:reminder",
      isSubscribed: true,
      group: "relation",
      type: "reminder",
      name: "Reminder",
      application: "autoremind",
      img: "/img/communications/category/general_message.png",
   },
   {
      contentType: "relation:birthday",
      isSubscribed: true,
      group: "relation",
      type: "birthday",
      name: "Birthday greeting",
      application: "communication",
      img: "/img/communications/category/birthday.png",
   },
   {
      contentType: "relation:latepayment",
      isSubscribed: true,
      group: "relation",
      type: "latepayment",
      name: "Late Payment",
      application: "communication",
      img: "/img/communications/category/latepayment.png",
   },
   {
      contentType: "relation:missed",
      isSubscribed: true,
      group: "relation",
      type: "missed",
      name: "Missed Appointment",
      application: "communication",
      img: "/img/communications/category/missed.png",
   },
   {
      contentType: "relation:triggered",
      isSubscribed: true,
      group: "relation",
      type: "triggered",
      name: "Triggered Message",
      application: "communication",
      img: "/img/communications/category/triggered.png",
   },
   {
      contentType: "relation:newsletter",
      isSubscribed: true,
      group: "relation",
      type: "newsletter",
      name: "Newsletter",
      application: "communication",
      img: "/img/communications/category/newsletter.png",
   },
   {
      contentType: "relation:recalls",
      isSubscribed: true,
      group: "relation",
      type: "recalls",
      name: "Recalls",
      application: "communication",
      img: "/img/communications/category/recalls.png",
   },
   {
      contentType: "commercial:sale",
      isSubscribed: true,
      group: "commercial",
      type: "sale",
      name: "Sales Messages",
      application: "communication",
      img: "/img/communications/category/sale.png",
   },
   {
      contentType: "relation:seasonal",
      isSubscribed: true,
      group: "relation",
      type: "seasonal",
      name: "Seasonal Greetings",
      application: "communication",
      img: "/img/communications/category/seasonal.png",
   },
   {
      contentType: "relation:shortnotice",
      isSubscribed: true,
      group: "relation",
      type: "shortnotice",
      name: "Short Notice",
      application: "communication",
      img: "/img/communications/category/shortnotice.png",
   },
   {
      contentType: "relation:survey",
      isSubscribed: true,
      group: "relation",
      type: "survey",
      name: "Survey",
      application: "communication",
      img: "/img/communications/category/survey.png",
   },
   {
      contentType: "relation:reviews",
      isSubscribed: true,
      group: "relation",
      type: "reviews",
      name: "Reviews",
      application: "communication",
      img: "/img/communications/category/reviews.png",
   },
];

@Component({
   selector: "subscriptions-dialog",
   templateUrl: "./subscriptions-dialog.component.html",
   styleUrls: ["./subscriptions-dialog.component.css"],
})
export class SubscriptionsDialogComponent implements OnInit {
   @Input() selectedDestinator: Destinator;
   @Input() selectedPatient: Person;

   @ViewChild(NgForm) subscriptionForm: NgForm;

   viewModel$: Observable<any>;
   saved$: Observable<boolean>;
   destinatorFilters$: Observable<Array<Partial<DestinatorFilter>>>;
   contentTypes: Array<ContentType>;
   contentTypes$: Observable<Array<ContentType>>;

   subscriptions: Subscription = new Subscription();
   filtersToUnsubscribe: Array<Partial<DestinatorFilter>> = [];

   constructor(
      public store: Store<any>,
      private modalService: BsModalService,
      private messageFilterSelectors: MessageFilterSelectors,
      public bsModalRef: BsModalRef
   ) {
      this.saved$ = this.messageFilterSelectors.selectSaved();
      this.destinatorFilters$ = this.messageFilterSelectors.selectDestinatorFilters();

      this.viewModel$ = combineLatest([
         this.messageFilterSelectors.selectLoading(),
         this.messageFilterSelectors.selectSaving(),
         this.messageFilterSelectors.selectSaved(),
      ]).pipe(map(([loading, saving, saved]) => ({ loading, saving, saved })));
   }

   ngOnInit(): void {
      this.store.dispatch(
         MessageFilterActions.getDestinatorFiltersByPersonId({ personId: this.selectedPatient.personId })
      );
      // TODO: add ngrx effect to show an error dialog and set an error state this dialog will react to and close the dialog (perhaps before showing error dialog)

      // Using ngForm provides a template driven form using ngModel to update the data model
      // We reactively generate the data model when filters are available
      console.log(this.selectedDestinator, "color: blue");
      this.contentTypes$ = this.destinatorFilters$.pipe(
         filter((destinatorFilters) => destinatorFilters !== undefined),
         map((destinatorFilters) => {
            // TODO: Filter types based on what is enabled on the account
            const contentTypes = [...availableContentTypes];
            contentTypes.forEach((contentType) => {
               const matchingFilters = destinatorFilters.filter(
                  (destinatorFilter) =>
                     contentType.contentType === destinatorFilter.contentType &&
                     destinatorFilter.destinatorType === this.selectedDestinator.type
               );
               // We may get multiple filters for the same cell phone (sms and voice)
               if (Boolean(matchingFilters) && matchingFilters.length > 0) {
                  contentType.isSubscribed = false;
                  contentType.destinatorFilterIds = matchingFilters.map((f) => f.destinatorFilterId);
               }

               if (Boolean(matchingFilters) && matchingFilters.length === 0) {
                  contentType.isSubscribed = true;
                  contentType.destinatorFilterIds = [];
               }
            });

            return contentTypes;
         })
      );

      // When saving - we can't get mutated data model of contentTypes$ so we feed the form the initial value
      // and when saving we just save the mutated data model
      this.contentTypes$.pipe(first()).subscribe((contentTypesFirst) => {
         this.contentTypes = contentTypesFirst;
      });
   }

   public onCancel(): void {
      if (this.subscriptionForm.dirty) {
         const confirmDiscardingModalRef = this.modalService.show(
            BsModalConfirmComponent,
            createConfirmModalOptions(
               "Discard Changes",
               "You have unsaved changes that will be lost. Are you sure you want to discard the changes?",
               "Yes",
               "No"
            )
         );

         confirmDiscardingModalRef.content.onClose
            .pipe(
               delay(200) // If no delay then ModalService will not hide correctly
            )
            .subscribe((dialogResult) => {
               if (dialogResult.isConfirmed) {
                  this.store.dispatch(
                     PatientsActions.openSubscriptionsDialogFromAngularJSSuccess({ isSubscriptionUpdated: false })
                  );
                  this.bsModalRef.hide();
               }
            });
      } else {
         this.bsModalRef.hide();
      }
   }

   saveChanges(): void {
      let filterIdsToDelete: Array<number> = [];

      this.contentTypes.forEach((contentType) => {
         if (contentType.isSubscribed) {
            if (contentType.destinatorFilterIds !== undefined && contentType.destinatorFilterIds.length > 0) {
               filterIdsToDelete = filterIdsToDelete.concat(contentType.destinatorFilterIds);
            }
         } else {
            if (contentType.destinatorFilterIds === undefined || contentType.destinatorFilterIds.length === 0) {
               this.addToUnsubscribe(contentType);
            }
         }
      });

      this.store.dispatch(
         MessageFilterActions.updateDestinatorFilters({
            destinatorFilters: this.filtersToUnsubscribe,
            destinatorFilterIds: filterIdsToDelete,
         })
      );

      // TODO: Consider moving this to OnInit? and ensure unsubscription
      this.saved$
         .pipe(
            filter((saved) => saved === true),
            first() // We should probably still unsubscribe if nothing saved!
         )
         .subscribe((saved) => {
            this.store.dispatch(PatientsActions.getSelectedPatient({ personId: this.selectedPatient.personId }));
            // TODO: should we send something from logs to differentiate between request?
            this.store.dispatch(
               PatientsActions.openSubscriptionsDialogFromAngularJSSuccess({ isSubscriptionUpdated: true })
            );
            this.bsModalRef.hide();
         });
   }

   addToUnsubscribe(contentType): void {
      switch (this.selectedDestinator.type) {
         case "email":
            this.filtersToUnsubscribe.push({
               applicationName: contentType.application,
               contentType: contentType.contentType,
               destinator: this.selectedDestinator.address,
               media: "email",
            });
            break;
         case "phonecell":
         case "phonehome":
         case "phonework":
         case "phonealt":
            this.filtersToUnsubscribe.push({
               applicationName: contentType.application,
               contentType: contentType.contentType,
               destinator: this.selectedDestinator.address,
               media: "sms",
            });
            this.filtersToUnsubscribe.push({
               applicationName: contentType.application,
               contentType: contentType.contentType,
               destinator: this.selectedDestinator.address,
               media: "voice",
            });
            break;
         default:
      }
   }
}
